import React, { useEffect } from "react";
import { Modal, Form, Input, Button } from "antd";
import { PaymentMilestoneStatus } from "../quotationTypes";
import { svg } from "../../../../Assets/svg";
import TextArea from "antd/es/input/TextArea";

interface Props {
  open: boolean;
  onClose: () => void;
  onSave: (values: any) => void;
  milestoneData: any;
  loading: boolean;
  currencySymbol?: string;
  conversionRate?: number;
}

const CreateMilestoneInvoiceModal: React.FC<Props> = ({
  open,
  onClose,
  onSave,
  milestoneData,
  loading,
  currencySymbol = "£",
  conversionRate = 1,
}) => {
  const [form] = Form.useForm();

  let totalInvoiced = 0;
  if (Array.isArray(milestoneData?.paid_details)) {
    totalInvoiced = milestoneData.paid_details.reduce(
      (sum: number, item: any) => sum + (Number(item.amount_paid) || 0),
      0,
    );
  }
  const calculated = Number(milestoneData?.calculated_amount) || 0;
  let remainingToInvoice = Number((calculated - totalInvoiced).toFixed(2));
  if (remainingToInvoice < 0) remainingToInvoice = 0;

  useEffect(() => {
    if (open && milestoneData) {
      if (
        milestoneData.paid_status === PaymentMilestoneStatus.PENDING ||
        milestoneData.paid_status === PaymentMilestoneStatus.PARTIAL
      ) {
        // Form default values
        form.setFieldsValue({
          amount: (remainingToInvoice * conversionRate).toFixed(2),
          notes: "",
        });
      }
    } else {
      form.resetFields();
    }
  }, [open, milestoneData, form]);

  const handleFinish = (values: any) => {
    onSave({
      amount: values.amount,
      notes: values.notes,
    });
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      title={
        <div className="text-center mb-4">
          <div className="fs-4 fw-bolder lh-m mb-2">
            Create Invoice
          </div>
          <div className="fs-6 fw-normal text-secondary">
            This will create a draft invoice for the entered amount.
          </div>
        </div>
      }
      closeIcon={svg?.closeIcon}
      width={"33.75rem"}
      className="commonModalClass"
      destroyOnClose
    >
      <Form form={form} layout="vertical" onFinish={handleFinish}>
        <div className="row g-3">
          <div className="col-12">
            <Form.Item
              className="mb-0"
              name="amount"
              label={<div className="custom-label mb-0 ff">Invoice Amount</div>}
              rules={[
                { required: true, message: "Please enter the invoice amount" },
                () => ({
                  validator(_, value) {
                    const amount = Number(value) / conversionRate;

                    let totalInvoiced = 0;
                    if (Array.isArray(milestoneData?.paid_details)) {
                      totalInvoiced = milestoneData.paid_details.reduce(
                        (sum: number, item: any) =>
                          sum + (Number(item.amount_paid) || 0),
                        0,
                      );
                    }
                    const calculated =
                      Number(milestoneData?.calculated_amount) || 0;
                    let remainingToInvoice = calculated - totalInvoiced;
                    if (remainingToInvoice < 0) remainingToInvoice = 0;

                    if (amount <= 0) {
                      return Promise.reject(
                        new Error("Amount must be greater than 0"),
                      );
                    }
                    const roundedAmount = Number(Number(amount).toFixed(2));
                    const roundedRemaining = Number(
                      Number(remainingToInvoice).toFixed(2),
                    );
                    if (roundedAmount > roundedRemaining) {
                      return Promise.reject(
                        new Error(
                          `Amount cannot exceed the remaining amount (${(roundedRemaining * conversionRate).toFixed(
                            2,
                          )})`,
                        ),
                      );
                    }
                    return Promise.resolve();
                  },
                }),
              ]}
            >
              <Input
                className="form-control form-control-lg amountInput"
                placeholder="0.00"
                prefix={<span className="text-white">{currencySymbol}</span>}
                onChange={(e) =>
                  form.setFieldValue(
                    "amount",
                    e.target.value.replace(/[^0-9.]/g, ""),
                  )
                }
              />
            </Form.Item>
            <div className="mt-2 text-secondary" style={{ fontSize: "14px" }}>
              <span className="fw-medium text-dark">Note:</span> This will create a draft invoice only. It will not be sent to the customer. After creation, you will be redirected to the invoice modification page.
            </div>
          </div>

          <div className="col-12 mt-4">
            <div className="row justify-content-center gx-3">
              <div className={remainingToInvoice > 0 ? "col-sm-5 col-4" : "col-12"}>
                <Button
                  className="btn btn-lg btn-outline-secondary w-100 h-auto"
                  onClick={onClose}
                  disabled={loading}
                >
                  CLOSE
                </Button>
              </div>
              {remainingToInvoice > 0 && (
                <div className="col-sm-7 col-8">
                  <Button
                    htmlType="submit"
                    className="btn btn-lg btn-primary w-100 h-auto"
                    loading={loading}
                  >
                    CREATE DRAFT INVOICE
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </Form>
    </Modal>
  );
};

export default CreateMilestoneInvoiceModal;
